import { useState, useEffect } from "react";
import { useParams, useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import useHttp from "../../hooks/use-http";
import { endPoints } from "../../config/end-points";
import { projectActions } from "../../store/project-store";
import { cuteToast } from "../UI/cute";
import SitemapCanvas from "./canvas/SitemapCanvas";
import ConfirmModal from "./ConfirmModal";
import { FiArrowLeft, FiArrowRight, FiLoader, FiSave, FiZap, FiCheck, FiImage, FiAlignLeft, FiX } from "react-icons/fi";
import "./SitemapEditor.css";

const SitemapEditor = () => {
  const { project_id } = useParams();
  const history = useHistory();
  const dispatch = useDispatch();
  const { sendRequest } = useHttp();

  const { currentProject, sitemap } = useSelector((state) => state.project);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [viewMode, setViewMode] = useState("text"); // "text" or "visual"
  const [loadingState, setLoadingState] = useState({
    show: false,
    message: "",
    subMessage: "",
    isComplete: false
  });

  // Leave confirmation modal state
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);

  // Navigate to dashboard with confirmation if content generation is in progress
  const handleBackToDashboard = () => {
    if (isGenerating) {
      setShowLeaveConfirm(true);
    } else {
      history.push('/');
    }
  };

  useEffect(() => {
    document.title = `${endPoints.title} | Sitemap Editor`;
    loadProject();
  }, [project_id]);

  const loadProject = () => {
    setIsLoading(true);
    sendRequest(
      { url: `${endPoints.projects}/${project_id}`, method: "GET" },
      (response) => {
        setIsLoading(false);
        if (response.status === 200) {
          dispatch(projectActions.setCurrentProject(response.body));
          if (response.body.sitemap_json) {
            try {
              dispatch(projectActions.setSitemap(JSON.parse(response.body.sitemap_json)));
            } catch (e) {
              cuteToast({
                type: "error",
                title: "Error",
                message: "Failed to load sitemap",
                timer: 3000,
              });
            }
          }
        }
      }
    );
  };

  const handleSitemapUpdate = (updatedSitemap) => {
    dispatch(projectActions.setSitemap(updatedSitemap));
    // Auto-save sitemap to backend
    if (currentProject?.id) {
      sendRequest(
        {
          url: `${endPoints.projects}/${currentProject.id}/sitemap/update`,
          method: "POST",
          body: { sitemap_json: JSON.stringify(updatedSitemap) },
        },
        () => {} // Silent save, no toast notification
      );
    }
  };

  const handleSaveSitemap = () => {
    if (!sitemap) return;

    setIsSaving(true);
    sendRequest(
      {
        url: `${endPoints.projects}/${project_id}/sitemap/update`,
        method: "POST",
        body: { sitemap_json: JSON.stringify(sitemap) },
      },
      (response) => {
        setIsSaving(false);
        if (response.status === 200) {
          cuteToast({
            type: "success",
            title: "Saved",
            message: "Sitemap saved successfully",
            timer: 3000,
          });
        } else {
          cuteToast({
            type: "error",
            title: "Save Failed",
            message: response.message || "Failed to save sitemap",
            timer: 3000,
          });
        }
      }
    );
  };

  // Generate sections for a new page using AI
  const handleGeneratePage = async (pageName, pageDescription) => {
    if (!currentProject?.id) return null;

    return new Promise((resolve) => {
      sendRequest(
        {
          url: `${endPoints.projects}/${currentProject.id}/sitemap/generate-page`,
          method: "POST",
          body: { page_name: pageName, page_description: pageDescription },
        },
        (response) => {
          if (response.status === 200) {
            cuteToast({
              type: "success",
              title: "Page Created",
              message: `"${pageName}" page has been added to your sitemap`,
              timer: 3000,
            });
            resolve(response.body);
          } else {
            cuteToast({
              type: "error",
              title: "Failed",
              message: response.message || "Failed to generate page",
              timer: 4000,
            });
            resolve(null);
          }
        }
      );
    });
  };

  const handleGenerateContent = () => {
    if (!currentProject?.id) return;

    // Calculate total sections for progress
    const totalSections = sitemap?.pages?.reduce((acc, page) => acc + (page.sections?.length || 0), 0) || 0;

    setIsGenerating(true);
    setLoadingState({
      show: true,
      message: "Generating website content...",
      subMessage: `AI is creating content for ${totalSections} sections across ${sitemap?.pages?.length || 0} pages`,
      totalSections,
      isComplete: false
    });
    dispatch(projectActions.setGenerationStep("generating-content"));

    sendRequest(
      {
        url: endPoints.projectGenerateContent,
        method: "POST",
        body: { project_id: currentProject.id },
      },
      (response) => {
        setIsGenerating(false);
        if (response.status === 200) {
          setLoadingState({
            show: true,
            message: "Content generated successfully!",
            subMessage: "Preparing your editor...",
            isComplete: true
          });

          dispatch(projectActions.setWireframes(response.body));
          dispatch(projectActions.setGenerationStep("complete"));

          cuteToast({
            type: "success",
            title: "Content Generated",
            message: "AI has generated content for all sections. Redirecting to editor...",
            timer: 4000,
          });
          setTimeout(() => {
            setLoadingState({ show: false, message: "", subMessage: "", isComplete: false });
            history.push(`/builder/edit/${currentProject.id}`);
          }, 1500);
        } else {
          dispatch(projectActions.setGenerationStep("idle"));
          setLoadingState({
            show: true,
            message: "Content Generation Failed",
            subMessage: response.message || "An error occurred while generating content. You can retry or go back to edit your sitemap.",
            isComplete: false,
            isError: true
          });
        }
      }
    );
  };

  if (isLoading) {
    return (
      <div className="sme-loading">
        <div className="sme-loading-inner">
          <div className="sme-loading-spinner" />
          <p>Loading project...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="sitemap-editor-container">
      {/* Header Toolbar */}
      <header className="sme-toolbar">
        <div className="sme-toolbar-left">
          {/* Logo */}
          <button onClick={handleBackToDashboard} className="sme-logo-btn">
            <img
              src={endPoints.logo}
              alt="Logo"
            />
          </button>

          <div className="sme-divider" />

          {/* Back to Dashboard */}
          <button onClick={handleBackToDashboard} className="sme-back-btn">
            <FiArrowLeft size={14} /> Back to Dashboard
          </button>

          <div className="sme-divider" />

          {/* Project Info */}
          <div className="sme-project-info">
            <h6>{currentProject?.name || "Website Structure"}</h6>
            <small>Design your sitemap</small>
          </div>
        </div>

        {/* View Mode Toggle */}
        <div className="sme-toolbar-center">
          <div className="sme-view-toggle">
            <button
              onClick={() => setViewMode("text")}
              className={`sme-view-btn ${viewMode === "text" ? "active-text" : ""}`}
            >
              <FiAlignLeft size={14} />
              Text
            </button>
            <button
              onClick={() => setViewMode("visual")}
              className={`sme-view-btn ${viewMode === "visual" ? "active-visual" : ""}`}
            >
              <FiImage size={14} />
              Visual
            </button>
          </div>
        </div>

        {/* Right Actions */}
        <div className="sme-toolbar-right">
          <button
            className="sme-save-btn"
            onClick={handleSaveSitemap}
            disabled={isSaving}
          >
            {isSaving ? (
              <>
                <FiLoader className="sme-spinner-icon" size={14} />
                Saving...
              </>
            ) : (
              <>
                <FiSave size={14} /> Save
              </>
            )}
          </button>
          <button
            className="sme-generate-btn"
            onClick={handleGenerateContent}
            disabled={isGenerating}
          >
            {isGenerating ? (
              <>
                <FiLoader className="sme-spinner-icon" size={14} />
                Generating...
              </>
            ) : (
              <>
                Generate Content <FiArrowRight size={14} />
              </>
            )}
          </button>
        </div>
      </header>

      {/* Canvas Area */}
      <div className="sme-canvas-area">
        {sitemap ? (
          <SitemapCanvas
            sitemap={sitemap}
            onUpdate={handleSitemapUpdate}
            niche={currentProject?.niche || "general"}
            darkMode={false}
            showZoomControls={true}
            projectId={currentProject?.id}
            onGeneratePage={handleGeneratePage}
            viewMode={viewMode}
            projectTheme={currentProject?.theme || "classic"}
          />
        ) : (
          <div className="sme-empty-state">
            <p>No sitemap found for this project</p>
          </div>
        )}
      </div>

      {/* Leave Confirmation Modal */}
      <ConfirmModal
        show={showLeaveConfirm}
        title="Generation in Progress"
        message="Content generation is currently running. Are you sure you want to leave? You may lose track of the progress."
        confirmText="Leave Anyway"
        cancelText="Stay"
        variant="warning"
        onConfirm={() => { setShowLeaveConfirm(false); history.push('/'); }}
        onCancel={() => setShowLeaveConfirm(false)}
      />

      {/* Full-screen Loading Overlay */}
      {loadingState.show && (
        <div className="sme-generation-overlay">
          <div className="sme-gen-content">
            <div className="sme-gen-spinner">
              <div className="sme-gen-icon">
                {loadingState.isError ? (
                  <FiX size={32} style={{ color: '#EF4444' }} />
                ) : loadingState.isComplete ? (
                  <FiCheck size={32} />
                ) : (
                  <FiZap size={28} />
                )}
              </div>
            </div>

            <h2 className="sme-gen-title">{loadingState.message}</h2>
            {!loadingState.isError && (
              <p className="sme-gen-subtitle">{loadingState.subMessage}</p>
            )}

            {loadingState.isError ? (
              <div className="sme-gen-steps">
                <div className="sme-gen-step step-error">
                  <div className="sme-step-indicator ind-error">!</div>
                  <div className="sme-step-text">
                    <div className="sme-step-title">Generation Failed</div>
                    <div className="sme-step-desc">{loadingState.subMessage}</div>
                  </div>
                </div>
                <div className="sme-error-actions">
                  <button
                    className="sme-error-back-btn"
                    onClick={() => {
                      setLoadingState({ show: false, message: "", subMessage: "", isComplete: false });
                    }}
                  >
                    <FiArrowLeft size={14} /> Back to Sitemap
                  </button>
                  <button
                    className="sme-error-retry-btn"
                    onClick={handleGenerateContent}
                  >
                    <FiZap size={14} /> Retry Generation
                  </button>
                </div>
              </div>
            ) : (
              <div className="sme-gen-steps">
                <div className={`sme-gen-step ${!loadingState.isComplete ? 'step-active' : 'step-completed'}`}>
                  <div className={`sme-step-indicator ${loadingState.isComplete ? 'ind-completed' : 'ind-active'}`}>
                    {loadingState.isComplete ? <FiCheck size={16} /> : '1'}
                  </div>
                  <div className="sme-step-text">
                    <div className="sme-step-title">Generating Content</div>
                    <div className="sme-step-desc">
                      Creating unique text, headlines, and descriptions for each section
                    </div>
                  </div>
                </div>
                <div className={`sme-gen-step ${loadingState.isComplete ? 'step-completed' : ''}`}>
                  <div className={`sme-step-indicator ${loadingState.isComplete ? 'ind-completed' : 'ind-pending'}`}>
                    {loadingState.isComplete ? <FiCheck size={16} /> : '2'}
                  </div>
                  <div className="sme-step-text">
                    <div className="sme-step-title">Preparing Editor</div>
                    <div className="sme-step-desc">
                      Setting up your visual editor with all generated content
                    </div>
                  </div>
                </div>
              </div>
            )}

            {!loadingState.isError && !loadingState.isComplete && (
              <div className="sme-gen-timer">
                This may take 1-2 minutes depending on the number of sections<span className="sme-dot-animation"></span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SitemapEditor;
